import { Component, EventEmitter, Input, Output, QueryList, ViewChildren } from '@angular/core'
import { CardComponent } from '@core/components/card/card.component'
import { IconButtonComponent } from '@core/components/icon-button/icon-button.component'
import { IconComponent } from '@core/components/icon/icon.component'
import { ButtonComponent } from '@core/components/button/button.component'
import { TableComponent } from '@core/components/table/table.component'
import { TableRowComponent } from '@core/components/table-row/table-row.component'
import { TableCellComponent } from '@core/components/table-cell/table-cell.component'
import { TextareaComponent } from '@core/components/textarea/textarea.component'
import { FormFieldComponent } from '@core/components/form-field/form-field.component'
import { OrderContractorPreviewComponent } from '../order-contractor-preview/order-contractor-preview.component'
import { TagComponent } from '@core/components/tag/tag.component'
import { CdkDragDrop, CdkDropList, DragDropModule, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop'
import { BaseComponent } from '@core/classes/base-component'
import { TooltipDirective } from '@core/directives/tooltip.directive'
import { FormGroup, FormsModule } from '@angular/forms'
import { CommonModule } from '@angular/common'
import {
	ConveyanceTypeRead,
	CurrentUserService,
	EntityReadShort,
	OrderPatch,
	OrderRead,
	OrderReadList,
	OrderReadListItem,
	OrdersService,
	StatusRead,
} from '@app/generated'
import { NotificationService } from '@core/services/notification.service'
import { OrdersFiltersInterface } from '../order-list/order-list.component'
import { LoaderService } from '@core/services/loader.service'
import { KanbanCard, KanbanColumn } from './../order-list/order-list.component'
import { finalize, forkJoin, map, Subscription } from 'rxjs'
import { PopoverComponent } from '@core/components/popover/popover.component'
import { ModalService } from '@core/services/modal.service'
import { OrderForm } from '@app/orders/forms/order/order.form'
import { Router, RouterLink } from '@angular/router'
import { ConfirmationComponent } from '@core/components/confirmation/confirmation.component'
import { SocketOrderResponse, SocketsService } from '@app/general/services/sockets.service'
import { fadeInOutDynamicAnimation } from '@core/animations/fading-entrances/fade-in.animation'
import { config } from '@config'
import { getItemTranslation } from '@app/general/utils/get-item-translation'
import { Permission } from '@app/acl/enums/permissions.enum'
import { AclService } from '@app/acl/services/acl.service'
import { OrdersInnerService } from '@app/orders/services/orders-inner.service'
import { getTextColor, isDark } from '@app/general/utils/color-manipulation'
import { ChangeConveyanceTypeComponent } from '@app/orders/forms/change-conveyance-type/change-conveyance-type.form'
import { OrderStatusUpdateComponent } from '../order-status-update/order-status-update.component'
import { ChangeStatusComponent } from '@app/orders/forms/change-status/change-status.form'

@Component({
	selector: 'order-kanban',
	standalone: true,
	templateUrl: './order-kanban.component.html',
	styleUrl: './order-kanban.component.scss',
	imports: [
		CardComponent,
		IconButtonComponent,
		IconComponent,
		ButtonComponent,
		TableComponent,
		TableRowComponent,
		TableCellComponent,
		TextareaComponent,
		FormFieldComponent,
		OrderContractorPreviewComponent,
		TagComponent,
		DragDropModule,
		TooltipDirective,
		FormsModule,
		CommonModule,
		PopoverComponent,
		RouterLink,
	],
	animations: [fadeInOutDynamicAnimation],
})
export class OrderKanbanComponent extends BaseComponent {
	childOptions: any
	filtersAreVisible = true
	dragging = false
	allColumns: CdkDropList<KanbanCard[]>[] = []
	orderPath: OrderPatch = {} as OrderPatch
	currentOffsets: Record<string, number> = {}
	maxOffsets: Record<string, number> = {}
	pageSize = 5
	allColumnsFull = false
	@Input() filters!: FormGroup<OrdersFiltersInterface>
	@Input() statuses: StatusRead[] = []
	@Input() columns: KanbanColumn[] = []
	@Input() contractorId: string | null = null
	@Input() openNewTaskModal!: ($event: MouseEvent) => void
	@Input() openPreviewTaskModal!: ($event: MouseEvent, item: KanbanCard) => void
	@ViewChildren(CdkDropList) dropLists!: QueryList<CdkDropList<KanbanCard[]>>
	@Output() changeView = new EventEmitter<void>()
	@Output() refreshList = new EventEmitter<void>()
	protected readonly OrderKanbanName = $localize`Add new order`
	protected readonly OrderKanbanRight = $localize``
	protected readonly SetPriorityText = $localize`Set priority`
	protected readonly AssignToText = $localize`Assign to`
	protected readonly ExportToCSVText = $localize`Export to CSV`
	protected readonly ShowOrderListText = $localize`Show order list`
	protected readonly textShowMoreCards = $localize`Show more cards`
	protected readonly textPaid = $localize`Paid`
	protected readonly getItemTranslation = getItemTranslation
	protected readonly textHasDeliveryConfirmation = $localize`Delivery confirmation`
	protected subscriptions: Subscription[] = []

	constructor(
		private ordersService: OrdersService,
		private notificationService: NotificationService,
		private loaderService: LoaderService,
		private modalService: ModalService,
		private socketsService: SocketsService,
		private router: Router,
		private aclService: AclService,
		private currentUserService: CurrentUserService,
		private ordersInnerService: OrdersInnerService,
	) {
		super()

		this.subscriptions.push(
			this.socketsService.orderUpdated$().subscribe((order: SocketOrderResponse) => {
				for (const column of this.columns) {
					const index = column.data.findIndex(elem => elem.order.id == order.order_id)
					if (index >= 0) {
						this.ordersService.getOrder(order.order_id).subscribe({
							next: (response: OrderRead) => {
								if (column.id != response.status?.id) {
									column.data.splice(index, 1)
									const newColumnIndex = this.columns.findIndex(elem => elem.id == response.status?.id)
									this.columns[newColumnIndex].data.push({ order: response })
								} else {
									column.data[index] = { order: response }
								}
							},
						})
					}
				}
			}),
		)
	}

	// TODO CHANGE TO PROMISES TO LOADER
	fetchOrders() {
		const canReadOrdersAction = () => {
			const observables = this.statuses.map(status => {
				if (!this.currentOffsets[status.id]) {
					this.currentOffsets[status.id] = 0
				}

				return this.ordersService
					.getOrders(
						this.currentOffsets[status.id],
						this.pageSize,
						this.filters.controls.search.value ?? undefined,
						false,
						undefined,
						undefined,
						this.filters.controls.takeDateFrom.value ?? undefined,
						this.filters.controls.takeDateTo.value ?? undefined,
						this.filters.controls.conveyanceType.value?.map(item => item.id) ?? undefined,
						undefined,
						this.filters.controls.entity.value?.map(item => item.id) ?? undefined,
						[status.id],
						this.contractorId
							? [this.contractorId]
							: this.filters.controls.contractor.value?.map(item => item.id) ?? undefined,
						undefined,
						undefined,
						undefined,
						this.filters.controls.status.value
							?.map(item => (item.category === 'container-logistic' ? item.id : undefined))
							.filter((id): id is string => id !== undefined) ?? undefined,
						this.filters.controls.status.value
							?.map(item => (item.category === 'container-clearance' ? item.id : undefined))
							.filter((id): id is string => id !== undefined) ?? undefined,
						this.filters.controls.status.value
							?.map(item => (item.category === 'order-payment' ? item.id : undefined))
							.filter((id): id is string => id !== undefined) ?? undefined,
						this.filters.controls.takenBy.value?.map(item => item.id) ?? undefined,
					)
					.pipe(
						map((response: OrderReadList) => {
							this.maxOffsets[status.id] = response.total
							const ordersForStatus = response.items.filter(item => item.status?.id === status.id)

							const column = this.columns.find(col => col.id === status.id)
							if (column) {
								column.data = [
									...column.data,
									...ordersForStatus.map(item => ({
										order: item,
									})),
								]
							}

							return column
						}),
					)
			})

			forkJoin(observables).subscribe({
				next: () => {
					this.allColumns = this.dropLists.toArray()
					this.dropLists.forEach((dropList, index) => {
						dropList.data = this.columns[index].data
						dropList.connectedTo = this.dropLists.filter((_, i) => i !== index)
					})

					this.allColumnsFull = this.statuses.every(
						status =>
							this.maxOffsets[status.id] <= this.pageSize ||
							this.currentOffsets[status.id] + this.pageSize >= this.maxOffsets[status.id],
					)
				},
				error: () => {
					const message = $localize`Was not able to fetch data`
					this.notificationService.error(message)
				},
			})
		}

		const canReadOwnOrdersAction = () => {
			const observables = this.statuses.map(status => {
				if (!this.currentOffsets[status.id]) {
					this.currentOffsets[status.id] = 0
				}

				return this.currentUserService
					.getOrders(
						this.currentOffsets[status.id],
						this.pageSize,
						this.filters.controls.search.value ?? undefined,
						false,
						undefined,
						undefined,
						this.filters.controls.takeDateFrom.value ?? undefined,
						this.filters.controls.takeDateTo.value ?? undefined,
						this.filters.controls.conveyanceType.value?.map(item => item.id) ?? undefined,
						this.filters.controls.takenBy.value?.map(item => item.id) ?? undefined,
						this.filters.controls.entity.value?.map(item => item.id) ?? undefined,
						[status.id],
						this.contractorId
							? [this.contractorId]
							: this.filters.controls.contractor.value?.map(item => item.id) ?? undefined,
						undefined,
						undefined,
						undefined,
						this.filters.controls.status.value
							?.map(item => (item.category === 'container-logistic' ? item.id : undefined))
							.filter((id): id is string => id !== undefined) ?? undefined,
						this.filters.controls.status.value
							?.map(item => (item.category === 'container-clearance' ? item.id : undefined))
							.filter((id): id is string => id !== undefined) ?? undefined,
						this.filters.controls.status.value
							?.map(item => (item.category === 'order-payment' ? item.id : undefined))
							.filter((id): id is string => id !== undefined) ?? undefined,
						this.filters.controls.takenBy.value?.map(item => item.id) ?? undefined,
					)
					.pipe(
						map((response: OrderReadList) => {
							this.maxOffsets[status.id] = response.total
							const ordersForStatus = response.items.filter(item => item.status?.id === status.id)

							const column = this.columns.find(col => col.id === status.id)
							if (column) {
								column.data = [
									...column.data,
									...ordersForStatus.map(item => ({
										order: item,
									})),
								]
							}

							return column
						}),
					)
			})

			forkJoin(observables).subscribe({
				next: () => {
					this.allColumns = this.dropLists.toArray()
					this.dropLists.forEach((dropList, index) => {
						dropList.data = this.columns[index].data
						dropList.connectedTo = this.dropLists.filter((_, i) => i !== index)
					})

					this.allColumnsFull = this.statuses.every(
						status =>
							this.maxOffsets[status.id] <= this.pageSize ||
							this.currentOffsets[status.id] + this.pageSize >= this.maxOffsets[status.id],
					)
				},
				error: () => {
					const message = $localize`Was not able to fetch data`
					this.notificationService.error(message)
				},
			})
		}

		if (this.aclCanReadOrder()) {
			canReadOrdersAction()
		} else {
			this.ordersInnerService.currentUserHasOrders().subscribe(hasOrders => {
				if (hasOrders) {
					canReadOwnOrdersAction()
				}
			})
		}
	}

	resetKanban() {
		this.currentOffsets = {}
		this.maxOffsets = {}
		this.allColumns = []
		this.columns = this.statuses.map(status => ({
			id: status.id ?? '',
			name: getItemTranslation(status) ?? '',
			color: status.colour ?? 'var(--color-dark)',
			data: [],
		}))
		this.fetchOrders()
	}

	deleteOrder(order: OrderReadListItem) {
		const id = order.id
		this.modalService.open(ConfirmationComponent).then(modal => {
			modal.title = $localize`Danger`
			modal.componentInstance.message = $localize`Are you sure to remove this order?`
		})

		this.modalService.modalRef.closed.subscribe((confirmed: boolean) => {
			if (confirmed) {
				this.ordersService.deleteOrder(id).subscribe({
					next: () => {
						const column = this.columns.find(c => c.data.some(o => o.order.id === id))
						if (column) {
							const orderIndex = column.data.findIndex(o => o.order.id === id)
							if (orderIndex > -1) {
								column.data.splice(orderIndex, 1)
							}
						}
						this.notificationService.success($localize`Order successfully deleted`)
						this.refreshList.emit()
					},
					error: () => {
						this.notificationService.error($localize`Was not able to delete order`)
						this.loaderService.disable()
					},
				})
			}
		})
	}

	fetchMoreOrders() {
		this.statuses.forEach(status => {
			if (this.currentOffsets[status.id] < this.maxOffsets[status.id]) {
				this.currentOffsets[status.id] += this.pageSize
			}
		})
		this.allColumnsFull = this.statuses.every(status => this.currentOffsets[status.id] + this.pageSize >= this.pageSize)
		this.fetchOrders()
	}

	onChangeView() {
		this.changeView.emit()
	}

	ngOnInit() {
		this.resetKanban()
	}

	drop(event: CdkDragDrop<KanbanCard[]>) {
		if (event.previousContainer === event.container) {
			moveItemInArray(event.container.data, event.previousIndex, event.currentIndex)
		} else {
			transferArrayItem(event.previousContainer.data, event.container.data, event.previousIndex, event.currentIndex)
			const orderId = event.container.data[event.currentIndex].order.id
			const containerId = event.container.id
			const index = this.allColumns.findIndex(column => column.id === containerId)
			const statusId = this.columns[index].id
			this.ordersService
				.patchOrder(orderId, {
					status_id: statusId,
				})
				.subscribe()
		}
	}

	aclCanReadOrder(): boolean {
		const permissions = [Permission.ORDERS_READ]
		return this.aclService.hasPermission(permissions)
	}

	aclCanCreateOrder(): boolean {
		const permissions = [Permission.ORDERS_CREATE]
		return this.aclService.hasPermission(permissions)
	}

	aclCanUpdateOrders(): boolean {
		const permissions = [Permission.ORDERS_UPDATE]
		return this.aclService.hasPermission(permissions)
	}

	aclCanDeleteOrder(): boolean {
		const permissions = [Permission.ORDERS_DELETE]
		return this.aclService.hasPermission(permissions)
	}

	aclCanCreateTasks(): boolean {
		const permissions = [Permission.TASKS_CREATE]
		return this.aclService.hasPermission(permissions)
	}

	async openAddOrderModal() {
		const modal = await this.modalService.open(OrderForm)
		modal.title = $localize`Add order`
		modal.iconName = 'add-order'
		modal.width = '1200px'
		modal.showConfirmationOnClickOutside = true
	}

	canGoToTheOrder(id: string): boolean {
		const serializedUrl: string = this.router.serializeUrl(this.router.createUrlTree([`/orders/${id}`]))
		const currentUrl: string = this.router.url.split('?')[0]
		return serializedUrl != currentUrl
	}

	async goToOrder(id: string): Promise<void> {
		if (this.canGoToTheOrder(id)) {
			this.loaderService.setContain().enable(async () => {
				await this.router.navigateByUrl(`/${config.modules.orders.path}/${id}`)
			})
		}
	}

	goToEntity(order: OrderReadListItem | undefined | null) {
		if (order && order.entity && order.entity.contractor && order.entity.contractor.id) {
			this.loaderService.setContain().enable(async () => {
				await this.router.navigateByUrl(`/contractors/${order.entity?.contractor?.id}`)
			})
		}
	}

	private patchData(orderId: string, statusId: string, event: CdkDragDrop<KanbanCard[]>) {
		this.orderPath = {
			status_id: statusId,
		}

		if (orderId && statusId) {
			this.ordersService
				.patchOrder(orderId, this.orderPath)
				.pipe(finalize(() => this.loaderService.disable()))
				.subscribe({
					next: () => {
						this.notificationService.info($localize`Order status has been updated`)
						this.refreshList.emit()
					},
					error: () => {
						this.notificationService.error($localize`Failed to patch order status`)
						transferArrayItem(
							event.container.data,
							event.previousContainer.data,
							event.currentIndex,
							event.previousIndex,
						)
					},
				})
		}
	}

	protected readonly textOrderPaymentStatus = $localize`Order payment status`
	protected readonly textContainerLogisticStatus = $localize`Container logistic status`
	protected readonly textContainerCustomsStatus = $localize`Container customs status`
	protected readonly textOrderStatus = $localize`Order status`

	aclCanReadStatuses(): boolean {
		const permissions = [Permission.STATUSES_READ]
		return this.aclService.hasPermission(permissions)
	}

	aclCanReadInvoices(): boolean {
		const permissions = [Permission.INVOICES_READ]
		return this.aclService.hasPermission(permissions)
	}

	aclCanReadCostInvoices(): boolean {
		const permissions = [Permission.COST_INVOICES_READ]
		return this.aclService.hasPermission(permissions)
	}

	/**
	 * Opens a modal to change the conveyance type of an order.
	 *
	 * @param order - The order for which the conveyance type is to be changed. Optional.
	 * @returns A promise that resolves when the modal is opened.
	 */
	async changeConveyanceType(order?: OrderReadListItem) {
		if (this.aclCanUpdateOrders()) {
			const modal = await this.modalService.open(ChangeConveyanceTypeComponent)
			modal.title = $localize`Change conveyance type`
			modal.iconName = 'show-all'
			modal.width = '400px'
			modal.componentInstance.order = order
			this.modalService.modalRef.afterClosed.subscribe((result: ConveyanceTypeRead | null | undefined) => {
				if (order && result) {
					order.conveyance_type = result
				}
			})
		}
	}

	/**
	 * Opens a modal to change the payment status of an order.
	 *
	 * @param {OrderReadListItem} [order] - The order for which the payment status is to be changed.
	 * @returns {Promise<void>} A promise that resolves when the modal is opened.
	 */
	async changePaymentStatus(order?: OrderReadListItem) {
		if (this.aclCanUpdateOrders()) {
			const modal = await this.modalService.open(OrderStatusUpdateComponent)
			modal.title = $localize`Change payment status`
			modal.iconName = 'show-all'
			modal.width = '400px'
			modal.componentInstance.order = order
			modal.componentInstance.category = 'order-payment'
			this.modalService.modalRef.afterClosed.subscribe(result => {
				if (order && result) {
					order.order_payment_status = result
				}
			})
		}
	}

	/**
	 * Changes the clearance status of an order.
	 *
	 * This method opens a modal dialog to update the clearance status of a container.
	 * If the user has the necessary permissions to update orders, the modal is displayed.
	 * The modal allows the user to change the container clearance status, and upon closing,
	 * the status is updated in the order if a result is provided.
	 *
	 * @param {OrderReadListItem} [order] - The order for which the clearance status is to be changed.
	 * @returns {Promise<void>} A promise that resolves when the modal is opened and the status is potentially updated.
	 */
	async changeClearanceStatus(order?: OrderReadListItem) {
		if (this.aclCanUpdateOrders()) {
			const modal = await this.modalService.open(OrderStatusUpdateComponent)
			modal.title = $localize`Change container clearance status`
			modal.iconName = 'show-all'
			modal.width = '400px'
			modal.componentInstance.order = order
			modal.componentInstance.category = 'container-clearance'
			this.modalService.modalRef.afterClosed.subscribe(result => {
				if (order && result) {
					order.container_clearance_status = result
				}
			})
		}
	}

	/**
	 * Opens a modal to change the logistic status of an order.
	 *
	 * @param {OrderReadListItem} [order] - The order for which the logistic status is to be changed.
	 * @returns {Promise<void>} A promise that resolves when the modal is opened.
	 *
	 * @remarks
	 * The modal allows the user to update the logistic status of a container.
	 * Once the modal is closed, if an order and a result are provided, the order's
	 * container logistic status is updated with the result.
	 */
	async changeLogisticStatus(order?: OrderReadListItem) {
		const modal = await this.modalService.open(OrderStatusUpdateComponent)
		modal.title = $localize`Change container logistic status`
		modal.iconName = 'show-all'
		modal.width = '400px'
		modal.componentInstance.order = order
		modal.componentInstance.category = 'container-logistic'
		this.modalService.modalRef.afterClosed.subscribe(result => {
			if (order && result) {
				order.container_logistic_status = result
			}
		})
	}

	/**
	 * Opens a modal to change the status of an order.
	 *
	 * @param {OrderReadListItem} [order] - The order for which the status is to be changed.
	 * @returns {Promise<void>} A promise that resolves when the modal is opened.
	 */
	async changeOrderStatus(order?: OrderReadListItem) {
		const modal = await this.modalService.open(ChangeStatusComponent)
		modal.title = $localize`Change order status`
		modal.iconName = 'show-all'
		modal.width = '400px'
		modal.componentInstance.order = order
		this.modalService.modalRef.afterClosed.subscribe(result => {
			if (order && result) {
				order.status = result
			}
		})
	}

	checkDateAndDsk(item: OrderReadListItem) {
		if (item.status?.is_finished) {
			return false
		}

		if (item.container_dsk && item.container_dsk.length > 0) {
			return true
		}

		if (item.entering_port_date) {
			const today = new Date()
			const enteringPortDate = new Date(item.entering_port_date)

			if (enteringPortDate <= today) {
				return true
			}
		}
		return false
	}

	protected readonly isDark = isDark
	protected readonly getTextColor = getTextColor

	showEntityName(item: EntityReadShort | null | undefined) {
		let entityName = ''
		if (item) {
			if (item.short_name) {
				entityName = item.short_name
			} else if (item.name) {
				let name: string = (item.name ?? '').slice(0, 16)
				if (item.name.length > 10) {
					name += '...'
				}
				entityName = name
			}
		}
		return entityName
	}

	ngOnDestroy() {
		if (this.subscriptions) {
			for (const subscription of this.subscriptions) {
				subscription.unsubscribe()
			}
		}
	}
}
